import { queueShowsQuickActions } from './quickActionsColumnRule';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useGT } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import type { ColumnConfigEntry, SystemEntity } from '@erp/shared-types';
import type { TaskTypeResponse } from '@erp/rpc';
import { AuthContext } from 'src/context/auth.context';
import { useCan } from 'src/hooks/useCan';
import { useColumnConfig } from 'src/hooks/useColumnConfig';
import type { SavedColumnsMode } from 'src/hooks/columnConfigMerge';
import { useTableFilterPreferences } from 'src/hooks/useTableFilterPreferences';
import { useEagerAndDebouncedState } from 'src/hooks/useEagerDebouncedState';
import { useUsersInOrg } from 'src/api/rpc/hooks/core/useUsersInOrg';
import { useListAgentDefinitions } from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import { useTaskTypes } from 'src/api/rpc/hooks/tasks/useTaskTypes';
import { useTaskQueue } from 'src/api/rpc/hooks/tasks/useTaskQueue';
import { useQueueAssigneePicker } from './useQueueAssigneePicker';
import { useTaskQueueBulkActions } from './useTaskQueueBulkActions';
import {
  TASKS_QUEUE_TABLE_ID,
  TASKS_QUEUE_DEFAULT_PAGE_SIZE,
  TASK_QUEUE_PERSISTED_FILTER_KEYS,
  TASK_QUEUE_SORT_FIELDS,
  TaskQueueFilterPrefsSchema,
  buildTaskListInput,
  buildTaskQueueFilterPrefs,
  type TaskQueueFilterPrefs,
  type TasksQueueSearch,
} from './taskQueueSearch';
import {
  queueSearchForDetail,
  taskDetailSearch,
  type TaskDetailSearch,
} from './taskDetailNav';
import { type TaskQueueCellLookup } from './taskQueueCells';
import { useTaskQueueColumns } from './useTaskQueueColumns';
import { queueShowsSnoozed } from './taskSnooze';
import { collectAnchorFields } from './columns/taskColumnOptions';
import {
  anchorReferenceColumns,
  anchorReferencesByPath,
  activeReferenceSelections,
  referenceEntityByApiField,
} from './columns/anchorReferenceColumns';
import { useTaskAnchorFieldCatalog } from 'src/api/rpc/hooks/tasks/useTaskAnchorFieldCatalog';

export const DEFAULT_TASK_QUEUE_COLUMNS: ColumnConfigEntry[] = [
  { id: 'displayId', header: 'ID', apiField: 'displayId' },
  { id: 'title', header: 'Title', apiField: 'title', sortable: true },
  { id: 'type', header: 'Type', apiField: 'taskTypeId' },
  { id: 'status', header: 'Status', apiField: 'status', sortable: true },
  { id: 'priority', header: 'Priority', apiField: 'priority', sortable: true },
  { id: 'assignee', header: 'Assignee', apiField: 'assigneeUserId' },
  { id: 'outcome', header: 'Outcome', apiField: 'outcome' },
  {
    id: 'dueDate',
    header: 'Due',
    apiField: 'dueDate',
    sortable: true,
    format: 'date',
  },
  {
    id: 'updatedAt',
    header: 'Updated',
    apiField: 'updatedAt',
    sortable: true,
    format: 'date',
  },
];

const SORTABLE_COLUMN_IDS = new Set<string>(TASK_QUEUE_SORT_FIELDS);

function toSortField(
  id: string | undefined,
): TasksQueueSearch['sortBy'] | undefined {
  return TASK_QUEUE_SORT_FIELDS.find((field) => field === id);
}

/** The task-type slice a page needs to pin the queue to one type. */
export type PinnedTaskType = Pick<TaskTypeResponse, 'id' | 'key' | 'name'>;

/**
 * Route-specific wiring for the queue controller. `/tasks` and each
 * `/pages/$taskPageId` mount supply their own URL state, preference table id,
 * and seed source; everything else about the queue behaves identically.
 */
export type TaskQueueControllerConfig = {
  /** Validated search params from the mounting route. */
  searchParams: TasksQueueSearch;
  /** Replace-navigate to the mounting route with the given search params. */
  navigateReplace: (search: TasksQueueSearch) => void;
  /** Key for saved column + filter preference rows. */
  tableId: string;
  defaultColumns: ColumnConfigEntry[];
  /**
   * Pin the queue to one task type (task pages). `?type=` in the URL is
   * ignored and the type filter should be hidden in the toolbar.
   */
  forcedType?: PinnedTaskType;
  /**
   * Filters applied when the URL carries none. `/tasks` seeds from the user's
   * saved preferences; task pages seed from the page's `defaultFilters` (the
   * URL always wins once the user changes a filter).
   */
  seedFilters?: TaskQueueFilterPrefs | null;
  /** Persist the user's filter changes (only `/tasks` does). */
  persistFilters: boolean;
  /**
   * When this queue is a task page, pass the page id so detail links include
   * `?fromPage=` and breadcrumbs can return here.
   */
  fromPageId?: string;
  /**
   * Task pages: the pinned type's anchor entity type. When set, visible
   * anchor columns are requested from `tasks.list` as `anchorFields`.
   */
  anchorEntityType?: SystemEntity | null;
  /**
   * How a user's saved columns combine with `defaultColumns` (see
   * SavedColumnsMode). Task pages use `addNewDefaults`, or `ignore` when the
   * page locks its columns; `/tasks` keeps the default `legacy` merge.
   */
  savedColumns?: SavedColumnsMode;
};

function useTaskQueueUrlState({
  searchParams: current,
  navigateReplace,
  tableId,
  persistFilters,
  seedFilters,
}: Pick<
  TaskQueueControllerConfig,
  | 'searchParams'
  | 'navigateReplace'
  | 'tableId'
  | 'persistFilters'
  | 'seedFilters'
>) {
  const { savedFilters, saveFilters, isPending } = useTableFilterPreferences(
    tableId,
    TaskQueueFilterPrefsSchema,
  );

  const didSeedRef = useRef(false);
  useEffect(() => {
    if (didSeedRef.current) return;
    if (persistFilters && isPending) return;
    didSeedRef.current = true;
    const urlHasFilters = TASK_QUEUE_PERSISTED_FILTER_KEYS.some(
      (key) => current[key] !== undefined,
    );
    if (urlHasFilters) return;
    const seed = persistFilters ? (savedFilters ?? {}) : (seedFilters ?? {});
    if (Object.keys(buildTaskQueueFilterPrefs(seed)).length === 0) return;
    navigateReplace({ ...current, ...seed });
  }, [
    isPending,
    savedFilters,
    seedFilters,
    persistFilters,
    current,
    navigateReplace,
  ]);

  function updateSearchParams(
    updates: Partial<TasksQueueSearch>,
    resetPage = false,
  ) {
    const merged: TasksQueueSearch = { ...current, ...updates };
    navigateReplace({
      ...current,
      ...updates,
      page: resetPage ? undefined : (updates.page ?? current.page),
    });
    if (
      persistFilters &&
      TASK_QUEUE_PERSISTED_FILTER_KEYS.some((key) => key in updates)
    ) {
      persistQueueFilters({ merged, savedFilters, saveFilters });
    }
  }

  return { updateSearchParams };
}

function persistQueueFilters({
  merged,
  savedFilters,
  saveFilters,
}: {
  merged: TasksQueueSearch;
  savedFilters: ReturnType<typeof buildTaskQueueFilterPrefs> | null | undefined;
  saveFilters: (prefs: ReturnType<typeof buildTaskQueueFilterPrefs>) => void;
}) {
  const nextPrefs = buildTaskQueueFilterPrefs(merged);
  if (
    JSON.stringify(nextPrefs) !==
    JSON.stringify(buildTaskQueueFilterPrefs(savedFilters ?? {}))
  ) {
    saveFilters(nextPrefs);
  }
}

function buildQueueLookups({
  taskTypes,
  usersData,
  agentDefinitions,
  gt,
  detailSearch,
}: {
  taskTypes: TaskTypeResponse[] | undefined;
  usersData: Array<{ id: string; name: string }> | undefined;
  agentDefinitions: Array<{ id: string; name: string }> | undefined;
  gt: ReturnType<typeof useGT>;
  detailSearch?: TaskDetailSearch;
}): TaskQueueCellLookup {
  return {
    typeNameById: new Map((taskTypes ?? []).map((t) => [t.id, t.name])),
    typeById: new Map((taskTypes ?? []).map((t) => [t.id, t])),
    userNameById: new Map((usersData ?? []).map((u) => [u.id, u.name])),
    agentNameById: new Map((agentDefinitions ?? []).map((a) => [a.id, a.name])),
    gt,
    detailSearch,
  };
}

function useSyncedQueueSearch({
  debouncedSearch,
  currentSearch,
  updateSearchParams,
}: {
  debouncedSearch: string;
  currentSearch: string | undefined;
  updateSearchParams: (
    updates: Partial<TasksQueueSearch>,
    resetPage?: boolean,
  ) => void;
}) {
  const updateRef = useRef(updateSearchParams);
  updateRef.current = updateSearchParams;

  useEffect(() => {
    const trimmed = debouncedSearch.trim();
    if ((currentSearch ?? '') === trimmed) return;
    updateRef.current({ search: trimmed || undefined }, true);
  }, [debouncedSearch, currentSearch]);
}

function resolveSelectedType(
  type: string | undefined,
  typeByKey: Map<string, TaskTypeResponse>,
): TaskTypeResponse | undefined {
  return type ? typeByKey.get(type) : undefined;
}

function taskTypesByKey(taskTypes: TaskTypeResponse[] | undefined) {
  return new Map((taskTypes ?? []).map((type) => [type.key, type]));
}

function queueDetailSearch({
  fromPageId,
  searchParams,
  debouncedSearch,
  selectedType,
}: {
  fromPageId?: string;
  searchParams: TasksQueueSearch;
  debouncedSearch: string;
  selectedType: PinnedTaskType | undefined;
}): TaskDetailSearch | undefined {
  return taskDetailSearch(
    fromPageId,
    queueSearchForDetail(
      {
        ...searchParams,
        search: debouncedSearch.trim() || undefined,
      },
      selectedType?.key,
    ),
  );
}

function queueEmptyMessage(
  error: unknown,
  gt: ReturnType<typeof useGT>,
): string {
  if (error) {
    return gt('Could not load tasks.', {
      context: 'error state on the tasks queue page',
    });
  }
  return gt('No tasks match the current filters.', {
    context: 'empty state on the tasks queue page',
  });
}

/**
 * State for the dialogs a queue row opens: snooze (one or many tasks) and
 * delete (one task), plus the show-snoozed toggle.
 */
function useQueueRowDialogs(input: {
  search: TasksQueueSearch;
  updateSearchParams: (
    updates: Partial<TasksQueueSearch>,
    resetPage?: boolean,
  ) => void;
}) {
  const [snoozeTaskIds, setSnoozeTaskIds] = useState<string[] | null>(null);
  const [deleteTaskId, setDeleteTaskId] = useState<string | null>(null);
  return {
    deleteTaskId,
    openDelete: setDeleteTaskId,
    openDeleteFor: (task: { id: string }) => setDeleteTaskId(task.id),
    closeDelete: () => setDeleteTaskId(null),
    showsSnoozed: queueShowsSnoozed(input.search),
    snoozeTaskIds,
    openSnooze: (taskIds: string[]) => setSnoozeTaskIds(taskIds),
    closeSnooze: () => setSnoozeTaskIds(null),
    setShowSnoozed: (show: boolean) => {
      input.updateSearchParams({ showSnoozed: show }, true);
    },
  };
}

function queueSortingState(searchParams: TasksQueueSearch) {
  if (!searchParams.sortBy) return [];
  return [
    {
      id: searchParams.sortBy,
      desc: (searchParams.sortOrder ?? 'desc') === 'desc',
    },
  ];
}

function queuePagination(searchParams: TasksQueueSearch, total: number) {
  const pageSize = searchParams.pageSize ?? TASKS_QUEUE_DEFAULT_PAGE_SIZE;
  return {
    pageSize,
    pageIndex: (searchParams.page ?? 1) - 1,
    pageCount: total > 0 ? Math.ceil(total / pageSize) : 1,
  };
}

/** Users, agents, and task types the queue renders and filters by. */
function useQueueDirectory() {
  const gt = useGT();
  const { currentUser } = useContext(AuthContext);
  const [canReadAgents] = useCan(Action.Read, Subject.BackgroundAgent);
  const { data: taskTypes } = useTaskTypes();
  const { data: usersData } = useUsersInOrg();
  const { data: agentData } = useListAgentDefinitions({
    limit: 50,
    enabled: canReadAgents,
  });
  const agentDefinitions = agentData?.definitions;
  const typeByKey = taskTypesByKey(taskTypes);
  return {
    gt,
    currentUserId: currentUser?.id,
    taskTypes,
    usersData,
    agentDefinitions,
    typeByKey,
  };
}

/**
 * Anchor columns on task pages (which pass their anchor type): the fields to
 * request from `tasks.list`, and the columns that reference another record
 * (an invoice's supplier) with the URL's `ref` picks they apply. The list
 * waits for the column config so the first call already carries the fields,
 * and while a `ref` pick waits on the field catalog, so it never loads
 * unfiltered first.
 */
function useAnchorColumns({
  anchorEntityType,
  configuredColumns,
  columnsLoading,
  ref,
}: {
  anchorEntityType: SystemEntity | null | undefined;
  configuredColumns: ColumnConfigEntry[];
  columnsLoading: boolean;
  ref: TasksQueueSearch['ref'];
}) {
  const catalog = useTaskAnchorFieldCatalog(anchorEntityType);
  if (anchorEntityType === undefined) {
    return {
      listInput: {},
      activeRef: undefined,
      waitingForColumns: false,
      referenceColumns: [],
      referenceEntityByApiField: new Map<string, SystemEntity>(),
    };
  }
  const anchorFields = collectAnchorFields(configuredColumns, anchorEntityType);
  const referenceColumns = anchorReferenceColumns(
    configuredColumns,
    anchorReferencesByPath(catalog.data?.fields),
  );
  // A failed catalog sends the picks as they are: the server validates them,
  // so the list errors instead of quietly showing every task.
  const activeRef = catalog.isError
    ? ref
    : activeReferenceSelections(ref, referenceColumns);
  // A type with no anchor has no catalog to wait for (and no reference columns).
  const waitsOnCatalog =
    anchorEntityType !== null &&
    Object.keys(ref ?? {}).length > 0 &&
    catalog.isPending;
  return {
    listInput: anchorFields.length > 0 ? { anchorFields } : {},
    activeRef,
    waitingForColumns: columnsLoading || waitsOnCatalog,
    referenceColumns,
    referenceEntityByApiField: referenceEntityByApiField(referenceColumns),
  };
}

/**
 * Shared queue state for any route that renders the tasks-queue experience.
 * `/tasks` mounts it via {@link useTasksQueuePage}; `/pages/$taskPageId`
 * mounts it pinned to the page's task type via `forcedType`.
 */
export function useTaskQueueController(config: TaskQueueControllerConfig) {
  const { searchParams, forcedType, fromPageId } = config;
  const directory = useQueueDirectory();
  const selectedType: PinnedTaskType | undefined =
    forcedType ?? resolveSelectedType(searchParams.type, directory.typeByKey);
  const { updateSearchParams } = useTaskQueueUrlState(config);
  const {
    eagerValue: searchInput,
    debouncedValue: debouncedSearch,
    setValue: setSearchInput,
  } = useEagerAndDebouncedState(searchParams.search ?? '', 300);

  useSyncedQueueSearch({
    debouncedSearch,
    currentSearch: searchParams.search,
    updateSearchParams,
  });

  const {
    columns: configuredColumns,
    addColumn,
    removeColumn,
    updateColumn,
    resetToDefaults,
    isLoading: columnsLoading,
  } = useColumnConfig(config.tableId, config.defaultColumns, {
    savedColumns: config.savedColumns,
  });
  const anchorColumns = useAnchorColumns({
    anchorEntityType: config.anchorEntityType,
    configuredColumns,
    columnsLoading,
    ref: searchParams.ref,
  });
  // Only the picks the toolbar shows filter the list and follow into detail.
  const queueSearch = { ...searchParams, ref: anchorColumns.activeRef };
  const detailSearch = queueDetailSearch({
    fromPageId,
    searchParams: queueSearch,
    debouncedSearch,
    selectedType,
  });
  const lookup = buildQueueLookups({
    taskTypes: directory.taskTypes,
    usersData: directory.usersData,
    agentDefinitions: directory.agentDefinitions,
    gt: directory.gt,
    detailSearch,
  });

  const listSearch = { ...queueSearch, search: debouncedSearch };
  const dialogs = useQueueRowDialogs({
    search: listSearch,
    updateSearchParams,
  });
  const listInput = {
    ...buildTaskListInput(listSearch, {
      currentUserId: directory.currentUserId,
      taskTypeId: selectedType?.id,
      anchorEntityType: config.anchorEntityType,
    }),
    ...anchorColumns.listInput,
  };
  const waitingForColumns = anchorColumns.waitingForColumns;
  const queueQuery = useTaskQueue(listInput, !waitingForColumns);
  const { tasks, total, snoozedCount, error } = queueQuery;
  const isLoading = queueQuery.isLoading || waitingForColumns;
  // Any change to the list query (page, filters, sort, search) is a new
  // selection scope; JSON of the input is a cheap, complete key for it.
  const bulk = useTaskQueueBulkActions(tasks, JSON.stringify(listInput));
  const assigneePicker = useQueueAssigneePicker({
    taskTypeId: selectedType?.id,
    statuses: searchParams.status,
    outcome: searchParams.outcome,
    excludeSnoozed: !dialogs.showsSnoozed,
    selectedAssignee: searchParams.assignee,
    fallbackUsers: directory.usersData,
    fallbackAgents: directory.agentDefinitions,
  });
  const pagination = queuePagination(searchParams, total);
  const cellLookup: TaskQueueCellLookup = {
    ...lookup,
    anchorReferences: anchorColumns.referenceEntityByApiField,
    onSnooze: (task) => dialogs.openSnooze([task.id]),
    onDelete: dialogs.openDeleteFor,
  };
  const columns = useTaskQueueColumns({
    configuredColumns,
    sortableColumnIds: SORTABLE_COLUMN_IDS,
    selectable: bulk.enabled,
    showQuickActions: queueShowsQuickActions(selectedType?.id, lookup.typeById),
  });

  return {
    searchParams,
    selectedType,
    fromPageId,
    detailSearch: lookup.detailSearch,
    searchInput,
    setSearchInput,
    updateSearchParams,
    taskTypes: directory.taskTypes,
    usersData: directory.usersData,
    agentDefinitions: directory.agentDefinitions,
    assigneePickerUsers: assigneePicker.users,
    assigneePickerAgents: assigneePicker.agents,
    configuredColumns,
    referenceColumns: anchorColumns.referenceColumns,
    addColumn,
    removeColumn,
    updateColumn,
    resetToDefaults,
    columns,
    cellLookup,
    bulk,
    openSnooze: dialogs.openSnooze,
    snoozeTaskIds: dialogs.snoozeTaskIds,
    closeSnooze: dialogs.closeSnooze,
    deleteTaskId: dialogs.deleteTaskId,
    openDelete: dialogs.openDelete,
    closeDelete: dialogs.closeDelete,
    showsSnoozed: dialogs.showsSnoozed,
    setShowSnoozed: dialogs.setShowSnoozed,
    tasks,
    total,
    snoozedCount,
    isLoading,
    emptyMessage: queueEmptyMessage(error, directory.gt),
    ...pagination,
    sortingState: queueSortingState(searchParams),
    toSortField,
  };
}

export type TaskQueueController = ReturnType<typeof useTaskQueueController>;

/** The `/tasks` route's queue: URL-driven type + persisted filter seeding. */
export function useTasksQueuePage() {
  const searchParams = useSearch({ from: '/authenticated/tasks' });
  const navigate = useNavigate();

  return useTaskQueueController({
    searchParams,
    navigateReplace: (search) => {
      void navigate({ to: '/tasks', search, replace: true });
    },
    tableId: TASKS_QUEUE_TABLE_ID,
    defaultColumns: DEFAULT_TASK_QUEUE_COLUMNS,
    persistFilters: true,
  });
}
